/**
 * VoiceSuggestionButton - Button to trigger voice suggestions for a character
 * 
 * Disabled until sufficient character information is available.
 * Opens VoiceSuggestionModal with matching voice results.
 */

import { useState, useMemo } from 'react'
import { Icon } from './Icon'
import { Button } from './Button'
import { ElevenLabsApiKeyModal } from './ElevenLabsApiKeyModal'
import { VoiceSuggestionModal } from './VoiceSuggestionModal'
import { useUserSettings } from '../../hooks/useUserSettings'
import { canSuggestVoice, suggestVoicesForCharacter } from '../../services/elevenlabs'
import './VoiceSuggestionButton.css'

/**
 * VoiceSuggestionButton Component
 * 
 * @param {object} character - Character data to analyze for voice suggestions
 * @param {function} onSelectVoice - Callback when a voice is selected
 * @param {string} className - Additional CSS classes
 */
export function VoiceSuggestionButton({ character, onSelectVoice, className = '' }) {
  const { getElevenLabsApiKey, hasElevenLabsApiKey } = useUserSettings()
  
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [showApiKeyModal, setShowApiKeyModal] = useState(false)
  const [showSuggestionsModal, setShowSuggestionsModal] = useState(false)
  const [suggestions, setSuggestions] = useState(null)

  // Check if we can suggest voices based on character data
  const { canSuggest, missingInfo } = useMemo(() => {
    return canSuggestVoice(character)
  }, [character])

  // Determine button state and tooltip
  const getButtonState = () => {
    if (loading) {
      return {
        disabled: true,
        tooltip: 'Analyzing character...',
        text: 'Analyzing...',
      }
    }
    
    if (!hasElevenLabsApiKey()) {
      return {
        disabled: false, // Allow click to prompt for API key
        tooltip: 'Set up ElevenLabs API key to get voice suggestions',
        text: 'Suggest Voice',
        needsApiKey: true,
      }
    }
    
    if (!canSuggest) {
      return {
        disabled: true,
        tooltip: `Add more details: ${missingInfo.join(', ')}`,
        text: 'Suggest Voice',
      }
    }
    
    return {
      disabled: false,
      tooltip: 'Get AI voice suggestions for this character',
      text: 'Suggest Voice',
    }
  }

  const buttonState = getButtonState()

  const handleClick = async () => {
    // If no API key, show the API key modal
    if (buttonState.needsApiKey) {
      setShowApiKeyModal(true)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const apiKey = getElevenLabsApiKey()
      const result = await suggestVoicesForCharacter(apiKey, character)
      
      if (result.error) {
        setError(result.error)
      } else {
        setSuggestions(result)
        setShowSuggestionsModal(true)
      }
    } catch (err) {
      console.error('Failed to get voice suggestions:', err)
      setError(err.message || 'Failed to get voice suggestions')
    } finally {
      setLoading(false)
    }
  }

  const handleSelectVoice = (voice) => {
    setShowSuggestionsModal(false)
    onSelectVoice?.(voice)
  }

  const handleApiKeySuccess = () => {
    setShowApiKeyModal(false)
    // After API key is set, user can click again to get suggestions
  }

  return (
    <>
      <div className={`voice-suggestion-button ${className}`}>
        <button
          type="button"
          className={`voice-suggestion-button__btn ${buttonState.disabled ? 'voice-suggestion-button__btn--disabled' : ''} ${loading ? 'voice-suggestion-button__btn--loading' : ''}`}
          onClick={handleClick}
          disabled={buttonState.disabled}
          title={buttonState.tooltip}
        >
          {loading ? (
            <span className="voice-suggestion-button__spinner" />
          ) : (
            <Icon name="volume" size={14} />
          )}
          <span>{buttonState.text}</span>
        </button>
        
        {error && (
          <span className="voice-suggestion-button__error" title={error}>
            <Icon name="alert" size={12} />
          </span>
        )}
      </div>

      {/* API Key Modal */}
      <ElevenLabsApiKeyModal
        isOpen={showApiKeyModal}
        onClose={() => setShowApiKeyModal(false)}
        onSuccess={handleApiKeySuccess}
      />

      {/* Suggestions Modal */}
      <VoiceSuggestionModal
        isOpen={showSuggestionsModal}
        onClose={() => setShowSuggestionsModal(false)}
        suggestions={suggestions}
        character={character}
        onSelectVoice={handleSelectVoice}
      />
    </>
  )
}

export default VoiceSuggestionButton
